/**
 * Sorts an array of objects by a specified property.
 *
 * @param {T[]} array - The array of objects to be sorted.
 * @param {(keyof T)[]} properties - The properties to sort by.
 * @param {boolean} [ascending=true] - Determines the sort order. Default is ascending.
 * @return {T[]} - The sorted array.
 */
export function sortByProperties<T>(array: T[], properties: (keyof T)[], ascending: boolean = true): T[] {
  return [...array].sort((a, b) => {
    for (const property of properties) {
      if (a[property] > b[property]) {
        return ascending ? 1 : -1;
      } else if (a[property] < b[property]) {
        return ascending ? -1 : 1;
      }
    }
    return 0;
  });
}

/**
 * Filters an array of objects by checking if the search value is found
 * in any of the provided property keys. The search is case-insensitive.
 *
 * @template T The type of the objects in the array.
 * @param {T[]} data - The array of objects to filter.
 * @param {string[]} keys - An array of property names to search within each object.
 * @param {string} search - The search string to match against.
 * @returns {T[]} A filtered array containing only the objects where
 *                at least one of the specified property values contains the search string.
 */
export function filterObjects<T extends Record<string, any>>(
  data: T[],
  keys: string[],
  search: string
): T[] {
  if (!search.trim()) {
    return data;
  }

  const lowerSearch = search.toLowerCase();

  return data.filter((item) =>
    keys.some((key) => {
      const value = item[key];
      return (
        value !== undefined &&
        value !== null &&
        String(value).toLowerCase().includes(lowerSearch)
      );
    })
  );
}
